/**
 * One Staffing Request: what was asked, the Pod as it stands now, and the decision.
 *
 * [RM] approves (choosing who goes on the Pod, defaulting to the requester's suggestion —
 * except a Swap, whose Resource is fixed) or rejects with a reason. The requester can withdraw while it is Pending. Approval is
 * checked here against the live Pod with the same planner the server runs, so a request
 * the Pod has moved past says why before the RM clicks — the server re-checks regardless.
 */

import { Link } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useApproveStaffingRequest,
  useRejectStaffingRequest,
  useStaffingRequest,
  useWithdrawStaffingRequest,
} from "@/hooks/use-staffing-requests";
import { addSeatOptions, planStaffingChange, type SourceSeat } from "@/lib/staffing-change";
import { cn } from "@/lib/utils";
import type {
  StaffingRequestDetailView,
  StaffingResourceOption,
} from "@/schemas/staffing-requests";
import { ResourceOptionPicker } from "./ResourceOptionPicker";
import {
  compositionLabel,
  formatCalendarDate,
  formatDate,
  seatLabel,
  sourceLabel,
  summarizeRequest,
} from "./request-copy";
import { StaffingStatusBadge } from "./StaffingStatusBadge";

interface Props {
  requestId: string;
}

export function StaffingRequestDetail({ requestId }: Props) {
  const { data, isLoading, isError, error, refetch } = useStaffingRequest(requestId);

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }
  if (isError || !data) {
    return (
      <PageContainer>
        <BackLink />
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load the request."}
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }
  return <RequestBody request={data} />;
}

function BackLink() {
  return (
    <Button variant="ghost" size="sm" className="self-start" asChild>
      <Link to="/dashboard/staffing-requests">
        <ArrowLeft className="mr-1.5 h-4 w-4" />
        Staffing Requests
      </Link>
    </Button>
  );
}

function RequestBody({ request }: { request: StaffingRequestDetailView }) {
  const withdraw = useWithdrawStaffingRequest(request.id);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);

  return (
    <PageContainer>
      <BackLink />
      <PageHeader
        title={summarizeRequest(request)}
        description={`${request.solution.name}${request.account ? ` · ${request.account.name}` : ""}`}
        actions={
          <div className="flex items-center gap-2">
            <StaffingStatusBadge status={request.status} />
            {request.canWithdraw && (
              <Button
                variant="outline"
                onClick={() => setConfirmWithdraw(true)}
                disabled={withdraw.isPending}
              >
                Withdraw
              </Button>
            )}
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <RequestCard request={request} />
        <div className="flex flex-col gap-4">
          <PodCard request={request} />
          {request.canDecide ? (
            <DecisionPanel request={request} />
          ) : (
            <DecisionCard request={request} />
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmWithdraw}
        onOpenChange={setConfirmWithdraw}
        title="Withdraw this request?"
        description="Resource Management will no longer see it as pending. You can raise a new request later."
        confirmLabel="Withdraw"
        loading={withdraw.isPending}
        onConfirm={() => withdraw.mutate(undefined, { onSuccess: () => setConfirmWithdraw(false) })}
      />
    </PageContainer>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="col-span-2 text-foreground">{children}</dd>
    </div>
  );
}

function RequestCard({ request }: { request: StaffingRequestDetailView }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Request</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="divide-y divide-border">
          <Row label="Kind">{request.kind}</Row>
          <Row label="Seat">{seatLabel(request.role, request.positionDisciplines)}</Row>
          {request.kind === "Swap" && (
            <>
              <Row label="Moving in">
                {request.suggestedResource?.name ?? "A Resource no longer in the directory"}
                {sourceLabel(request) && (
                  <span className="text-muted-foreground"> from {sourceLabel(request)}</span>
                )}
              </Row>
              {!request.target && <Row label="Replacing">Nobody — an open seat</Row>}
            </>
          )}
          {request.target && (
            <Row label={request.kind === "Remove" ? "Removing" : "Replacing"}>
              {request.target.resource?.name ?? "A Resource no longer in the directory"}
            </Row>
          )}
          {request.kind !== "Remove" && (
            <>
              <Row label="Discipline">
                {request.discipline
                  ? `${request.discipline.name} (${request.discipline.code})`
                  : "No preference"}
              </Row>
              {request.kind !== "Swap" && (
                <Row label="Suggested">
                  {request.suggestedResource?.name ?? "None — RM to choose"}
                </Row>
              )}
            </>
          )}
          <Row label="Priority">{request.priority?.name ?? "None"}</Row>
          <Row label="Needed by">
            {request.neededBy ? formatCalendarDate(request.neededBy) : "Not specified"}
          </Row>
          <Row label="Requested">
            {request.requestedBy?.name ?? "Unknown"} · {formatDate(request.createdAt)}
          </Row>
          <Row label="Reason">
            <span className="whitespace-pre-wrap">{request.reason}</span>
          </Row>
        </dl>
      </CardContent>
    </Card>
  );
}

function PodCard({ request }: { request: StaffingRequestDetailView }) {
  const { pod } = request;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Pod now · {compositionLabel(pod.composition)}</CardTitle>
      </CardHeader>
      <CardContent>
        {pod.members.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nobody is assigned to this Pod.</p>
        ) : (
          <ul className="divide-y divide-border">
            {pod.members.map((m) => {
              const affected = request.target?.assignmentId === m.assignmentId;
              return (
                <li
                  key={m.assignmentId}
                  className={cn(
                    "flex items-center justify-between gap-2 py-1.5 text-sm",
                    affected && request.status === "Pending" && "font-semibold",
                  )}
                >
                  <span>{m.resourceName}</span>
                  <span className="text-xs text-muted-foreground">
                    {seatLabel(m.role, request.positionDisciplines)}
                    {affected &&
                      request.status === "Pending" &&
                      ` · ${request.kind.toLowerCase()} requested`}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function DecisionCard({ request }: { request: StaffingRequestDetailView }) {
  if (request.status === "Pending") {
    return (
      <Card>
        <CardContent className="text-sm text-muted-foreground">
          Waiting for Resource Management to decide.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Decision</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="divide-y divide-border">
          <Row label="Outcome">
            <StaffingStatusBadge status={request.status} />
          </Row>
          <Row label="By">
            {request.decidedBy?.name ?? "Unknown"}
            {request.decidedAt && ` · ${formatDate(request.decidedAt)}`}
          </Row>
          {request.status === "Approved" && request.kind !== "Remove" && (
            <Row label="Placed">{request.fulfilledResource?.name ?? "—"}</Row>
          )}
          {request.decisionNote && (
            <Row label={request.status === "Rejected" ? "Reason" : "Note"}>
              <span className="whitespace-pre-wrap">{request.decisionNote}</span>
            </Row>
          )}
        </dl>
      </CardContent>
    </Card>
  );
}

/** What approval will do, in a sentence — so the RM approves an outcome, not a form. */
function outcomeSentence(
  request: StaffingRequestDetailView,
  chosen: StaffingResourceOption | null,
): string {
  const who = chosen?.name ?? "the Resource you choose";
  const seat = seatLabel(request.role, request.positionDisciplines);
  if (request.kind === "Remove") {
    const leaves = `${request.target?.resource?.name ?? "The Pod member"} comes off ${seat}. The seat stays open.`;
    // §4.3: a Solution with no R1 has no Fit Score — say so before the RM approves it.
    return request.role === "R1"
      ? `${leaves} With ${seat} empty the Solution shows as Unstaffed and has no Fit Score until it is filled.`
      : leaves;
  }
  if (request.kind === "Replace") {
    return `${request.target?.resource?.name ?? "The Pod member"} comes off ${seat} and ${who} takes it.`;
  }
  if (request.kind === "Swap") {
    const from = sourceLabel(request);
    const leaves = from ? ` ${who} leaves ${from}, which opens up.` : "";
    const lands = request.target
      ? `${request.target.resource?.name ?? "The Pod member"} comes off ${seat} and ${who} takes it.`
      : `${who} takes ${seat}.`;
    return `${lands}${leaves}`;
  }
  if (request.role === "Supporting") return `${who} joins as Supporting.`;
  const grows = addSeatOptions(request.pod).find((o) => o.role === request.role)?.expandsTo;
  return grows
    ? `The Pod grows to a ${grows} and ${who} takes ${seat}.`
    : `${who} takes the open ${seat} seat.`;
}

function DecisionPanel({ request }: { request: StaffingRequestDetailView }) {
  const approve = useApproveStaffingRequest(request);
  const reject = useRejectStaffingRequest(request.id);
  // The requester's suggestion is the default; the RM may pick someone else or clear it.
  const [chosen, setChosen] = useState<StaffingResourceOption | null>(
    request.suggestedResource
      ? { id: request.suggestedResource.id, name: request.suggestedResource.name, disciplines: [] }
      : null,
  );
  const [note, setNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [mode, setMode] = useState<"approve" | "reject">("approve");

  const needsResource = request.kind !== "Remove";
  // A Swap moves the Resource it names; the RM approves or rejects, never substitutes.
  const fixedResource = request.kind === "Swap";
  const resourceId = needsResource ? (chosen?.id ?? null) : null;
  // The server re-checks the source seat live; here it is taken as the request recorded it.
  const source: SourceSeat | null =
    request.kind === "Swap" && request.source?.assignmentId && request.source.solution && resourceId
      ? {
          assignmentId: request.source.assignmentId,
          solutionId: request.source.solution.id,
          resourceId,
          role: request.source.role ?? "Supporting",
          active: true,
        }
      : null;
  const plan = planStaffingChange(
    {
      kind: request.kind,
      role: request.role,
      targetAssignmentId: request.target?.assignmentId ?? null,
    },
    request.pod,
    resourceId ? { id: resourceId, active: true } : null,
    source,
  );
  const stale = !plan.ok && (!needsResource || resourceId !== null) ? plan.reason : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Decide</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={mode === "approve" ? "default" : "outline"}
            onClick={() => setMode("approve")}
          >
            Approve
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === "reject" ? "default" : "outline"}
            onClick={() => setMode("reject")}
          >
            Reject
          </Button>
        </div>

        {mode === "approve" ? (
          <>
            {needsResource && !fixedResource && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="placed-resource">Who goes on the Pod?</Label>
                <ResourceOptionPicker
                  id="placed-resource"
                  solutionId={request.solution.id}
                  selected={chosen}
                  onChange={setChosen}
                  excludeIds={request.pod.members.map((m) => m.resourceId)}
                  suggestedId={request.suggestedResource?.id ?? null}
                />
              </div>
            )}
            <p className="text-sm">{outcomeSentence(request, chosen)}</p>
            {stale && (
              <p className="flex items-start gap-1.5 text-sm text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {stale}
              </p>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="approve-note">Note (optional)</Label>
              <Textarea
                id="approve-note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <Button
              className="self-start"
              disabled={approve.isPending || !plan.ok}
              onClick={() =>
                approve.mutate({ resourceId, note: note.trim() ? note.trim() : undefined })
              }
            >
              {approve.isPending ? "Approving…" : "Approve and update Pod"}
            </Button>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reject-reason">Reason</Label>
              <Textarea
                id="reject-reason"
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="The requester sees this."
              />
            </div>
            <Button
              className="self-start"
              variant="destructive"
              disabled={reject.isPending || rejectReason.trim().length === 0}
              onClick={() => reject.mutate(rejectReason.trim())}
            >
              {reject.isPending ? "Rejecting…" : "Reject request"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
